import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SignatureCanvas from 'react-signature-canvas';
import { X, RotateCcw, AlertCircle } from 'lucide-react';

// ── Capture Sign-Off Modal ──────────────────────────────────────────────────

interface SignOffCaptureModalProps {
  isOpen: boolean;
  onConfirm: (signerName: string, signatureDataUrl: string) => Promise<void>;
  onWaive: () => void;
  onClose: () => void;
  submitting?: boolean;
  error?: string | null;
}

export function SignOffCaptureModal({
  isOpen,
  onConfirm,
  onWaive,
  onClose,
  submitting,
  error: externalError,
}: SignOffCaptureModalProps) {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [signerName, setSignerName] = useState('');
  const [hasSigned, setHasSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 200 });
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);

  const isSubmitting = submitting || localSubmitting;
  const displayError = externalError || localError;

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Clear local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalError(null);
      setLocalSubmitting(false);
    }
  }, [isOpen]);

  // Measure canvas container once when modal opens.
  // IMPORTANT: Do NOT listen for window resize events — changing the canvas
  // width/height attributes clears its bitmap (browser spec), which destroys
  // the customer's drawn signature on mobile (keyboard dismiss, browser
  // chrome changes, etc. all fire resize).
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: Math.max(180, rect.height) });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  function handleClear() {
    sigCanvasRef.current?.clear();
    setHasSigned(false);
  }

  async function handleConfirm() {
    if (!sigCanvasRef.current || !signerName.trim() || !hasSigned) return;
    setLocalError(null);
    setLocalSubmitting(true);
    try {
      const canvas = sigCanvasRef.current.getCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      await onConfirm(signerName.trim(), dataUrl);
    } catch (err) {
      console.error('[sign-off] confirm failed:', err);
      setLocalError(
        err instanceof Error ? err.message : 'Failed to save sign-off. Please try again.'
      );
    } finally {
      setLocalSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const canSubmit = hasSigned && signerName.trim().length > 0 && !isSubmitting;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Customer Sign-Off"
    >
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-stone">
        <h2 className="font-display text-h3 text-midnight font-semibold">Customer Sign-Off</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-11 h-11 flex items-center justify-center rounded-xl bg-mist text-midnight
                     hover:bg-stone/40 active:bg-stone/60 transition-colors"
          aria-label="Close"
        >
          <X size={22} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col overflow-y-auto px-4 pt-4 pb-6 gap-4">
        {/* Instructions */}
        <p className="text-[13px] text-slate leading-relaxed">
          Please hand the device to the customer. Customer: type your name and sign below to confirm receipt.
        </p>

        {/* Signer name */}
        <div>
          <label htmlFor="signer-name" className="block text-[12px] font-semibold text-midnight mb-1.5">
            Full Name
          </label>
          <input
            id="signer-name"
            type="text"
            value={signerName}
            onChange={e => setSignerName(e.target.value)}
            placeholder="Type customer's full name"
            className="w-full h-11 px-3 rounded-xl border border-stone text-small text-midnight
                       placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum"
            disabled={isSubmitting}
          />
        </div>

        {/* Signature canvas */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[12px] font-semibold text-midnight">Signature</label>
            <button
              type="button"
              onClick={handleClear}
              disabled={isSubmitting}
              className="text-[11px] text-plum font-medium hover:underline flex items-center gap-1"
            >
              <RotateCcw size={11} /> Clear
            </button>
          </div>
          <div
            ref={containerRef}
            className="flex-1 min-h-[180px] border-2 border-dashed border-stone rounded-xl bg-mist/50 relative overflow-hidden"
          >
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="#1e1b4b"
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: 'touch-none',
              }}
              onEnd={() => setHasSigned(true)}
            />
          </div>
          <p className="text-[11px] text-slate mt-1 text-center">Sign with your finger above</p>
        </div>
      </div>

      {/* Error message */}
      {displayError && (
        <div className="shrink-0 px-4 pb-2">
          <div className="px-3 py-2.5 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose flex items-start gap-2">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{displayError}</span>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="shrink-0 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-stone flex flex-col gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={onWaive}
          disabled={isSubmitting}
          className="btn-secondary text-[13px]"
        >
          Waive Sign-Off
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canSubmit}
          className="btn-primary text-[13px]"
        >
          {isSubmitting ? 'Saving…' : 'Confirm Sign-Off'}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Waive Sign-Off Modal ────────────────────────────────────────────────────

interface WaiveSignOffModalProps {
  isOpen: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  submitting?: boolean;
}

export function WaiveSignOffModal({
  isOpen,
  onConfirm,
  onClose,
  submitting,
}: WaiveSignOffModalProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waive-signoff-title"
    >
      <div className="absolute inset-0 bg-midnight/70" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4">
        <h2 id="waive-signoff-title" className="font-display text-h3 text-midnight font-semibold">
          Waive Sign-Off
        </h2>
        <p className="text-small text-slate leading-relaxed">
          Please explain why the customer sign-off is being waived.
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Customer was unavailable at time of delivery"
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-stone text-small text-midnight
                     placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum resize-none"
          disabled={submitting}
        />
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!reason.trim() || submitting}
            className="flex-1 btn-danger"
          >
            {submitting ? 'Saving…' : 'Confirm Waive'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Sign-Off Detail Modal ───────────────────────────────────────────────────

interface SignOffDetailModalProps {
  isOpen: boolean;
  signOff: {
    status: 'captured' | 'waived';
    signerName?: string;
    signatureUrl?: string;
    waiveReason?: string;
    capturedBy: string;
    capturedAt: string;
  };
  onCaptureSignOff?: () => void;
  onClose: () => void;
}

export function SignOffDetailModal({
  isOpen,
  signOff,
  onCaptureSignOff,
  onClose,
}: SignOffDetailModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isCaptured = signOff.status === 'captured';
  const timestamp = new Date(signOff.capturedAt).toLocaleString();

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signoff-detail-title"
    >
      <div className="absolute inset-0 bg-midnight/70" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 id="signoff-detail-title" className="font-display text-h3 text-midnight font-semibold">
            {isCaptured ? 'Customer Sign-Off' : 'Sign-Off Waived'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {isCaptured ? (
          <div className="flex flex-col gap-3">
            {signOff.signerName && (
              <div>
                <p className="text-[11px] font-medium text-slate uppercase tracking-wider">Signed by</p>
                <p className="text-small text-midnight font-medium mt-0.5">{signOff.signerName}</p>
              </div>
            )}
            {signOff.signatureUrl && (
              <div>
                <p className="text-[11px] font-medium text-slate uppercase tracking-wider mb-1">Signature</p>
                <div className="border border-stone rounded-xl p-2 bg-mist/30">
                  <img src={signOff.signatureUrl} alt="Customer signature" className="max-w-full h-auto" />
                </div>
              </div>
            )}
            <div>
              <p className="text-[11px] font-medium text-slate uppercase tracking-wider">Captured at</p>
              <p className="text-[13px] text-midnight mt-0.5">{timestamp}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {signOff.waiveReason && (
              <div>
                <p className="text-[11px] font-medium text-slate uppercase tracking-wider">Reason</p>
                <p className="text-small text-midnight mt-0.5">{signOff.waiveReason}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] font-medium text-slate uppercase tracking-wider">Waived at</p>
              <p className="text-[13px] text-midnight mt-0.5">{timestamp}</p>
            </div>
            {onCaptureSignOff && (
              <button
                type="button"
                onClick={() => { onClose(); onCaptureSignOff(); }}
                className="btn-primary text-[13px] mt-1"
              >
                Capture Customer Sign-Off
              </button>
            )}
          </div>
        )}

        <button type="button" onClick={onClose} className="btn-secondary mt-1">
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
